import { useListAttendanceSessions } from "@workspace/api-client-react";
import type { AttendanceSession } from "@workspace/api-client-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

const IST = "Asia/Kolkata";

export function fmtTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("en-IN", {
    timeZone: IST,
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function fmtDay(d: string | null | undefined): string {
  if (!d) return "—";
  // sessionDate is a plain YYYY-MM-DD (already IST calendar date)
  const [y, m, day] = d.slice(0, 10).split("-").map(Number);
  if (!y || !m || !day) return d;
  return new Date(y, m - 1, day).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function fmtDuration(min: number | null | undefined): string {
  if (min == null) return "—";
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export function statusBadge(status: AttendanceSession["status"]) {
  const variant =
    status === "open" ? "default" : status === "anomalous" ? "destructive" : "secondary";
  const label = status === "open" ? "Inside" : status === "anomalous" ? "Check" : "Closed";
  return <Badge variant={variant}>{label}</Badge>;
}

// Read-only attendance table — used by the account-detail "Attendance" tab
// (accountId, all members of the account) and by the Member detail page
// (Phase 37, contactId — this member's sessions only). Exactly one of
// accountId/contactId is expected; when contactId is set it takes priority
// (existing accountId-only callers are unaffected).
export function AttendanceList({
  accountId,
  contactId,
}: {
  accountId?: number;
  contactId?: number;
}) {
  const { data, isLoading } = useListAttendanceSessions(
    contactId != null ? { contactId } : { accountId },
  );
  const sessions = data?.items ?? [];

  return (
    <div className="space-y-3 pt-2">
      <p className="text-xs text-muted-foreground">
        Entry/exit captured from the biometric device. A member appears here once their
        enrollment ID is set as <span className="font-medium">Biometric ID</span> on the Members
        tab. Showing the last 30 days.
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Member</TableHead>
            <TableHead>Entry</TableHead>
            <TableHead>Exit</TableHead>
            <TableHead>Duration</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-6">
                Loading…
              </TableCell>
            </TableRow>
          ) : sessions.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-6">
                No attendance recorded in the last 30 days.
              </TableCell>
            </TableRow>
          ) : (
            sessions.map((s) => (
              <TableRow key={s.id}>
                <TableCell>{fmtDay(s.sessionDate)}</TableCell>
                <TableCell>{s.contactName ?? "—"}</TableCell>
                <TableCell className="tabular-nums">{fmtTime(s.entryAt)}</TableCell>
                <TableCell className="tabular-nums">{fmtTime(s.exitAt)}</TableCell>
                <TableCell className="tabular-nums">{fmtDuration(s.durationMinutes)}</TableCell>
                <TableCell>{statusBadge(s.status)}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
